import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';

function ApplicantList() {
  const [rawApplications, setRawApplications] = useState([]);
  const [rawArchivedApplications, setRawArchivedApplications] = useState([]);
  const [propertyFilter, setPropertyFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [lastViewedAt, setLastViewedAt] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hideNewItems, setHideNewItems] = useState(localStorage.getItem('dismiss_newitems_landlord_applicants') === '1');
  const [showArchived, setShowArchived] = useState(false);
  const navigate = useNavigate();

  const statusLabels = {
    submitted: 'Submitted',
    in_review: 'In Review',
    docs_requested: 'Docs Requested',
    info_needed: 'In Review',
    approved: 'Approved',
    lease_drafted_landlord_signed: 'Approved',
    lease_tenant_signed: 'Tenant signed',
    completed: 'Completed',
    rejected: 'Rejected'
  };

  const groupApplications = (list) => list.reduce((acc, app) => {
    const key = `${app.property_address}, ${app.property_city}`;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(app);
    return acc;
  }, {});

  const getPropertyLabel = (app) => `${app.property_address}, ${app.property_city}`;

  const getStatusChipClass = (status) => {
    if (['approved', 'completed', 'lease_tenant_signed'].includes(status)) return 'report-chip report-chip--success';
    if (['rejected', 'declined'].includes(status)) return 'report-chip report-chip--warn';
    if (['docs_requested', 'in_review', 'info_needed', 'action_required'].includes(status)) return 'report-chip report-chip--info';
    return 'report-chip report-chip--muted';
  };

  useEffect(() => {
    const fetchApplicants = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }
      try {
        const [countRes, response, archivedRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/applications/unread-count`, {
            headers: { 'Authorization': `Bearer ${token}` },
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/applications`, {
            headers: { 'Authorization': `Bearer ${token}` },
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/applications?archived=1`, {
            headers: { 'Authorization': `Bearer ${token}` },
          })
        ]);
        if (countRes.ok) {
          const countData = await countRes.json().catch(() => ({}));
          setLastViewedAt(countData.last_viewed_at || null);
          setUnreadCount(countData.count || 0);
        }
        if (!response.ok) throw new Error('Failed to fetch applicants');
        const data = await response.json();
        const archivedData = archivedRes.ok ? await archivedRes.json().catch(() => ({})) : { applications: [] };
        await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/applications/mark-viewed`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
        });
        setUnreadCount(0);
        window.dispatchEvent(new Event('applications:viewed'));

        const list = data.applications || [];
        const archivedList = archivedData.applications || [];
        setRawApplications(list);
        setRawArchivedApplications(archivedList);

        // Group applications by property
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchApplicants();
  }, [navigate]);

  const latestApplications = [...rawApplications]
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 3);
  const archivedCount = rawArchivedApplications.length;

  const propertyOptions = useMemo(() => {
    const labels = new Set([...rawApplications, ...rawArchivedApplications].map(getPropertyLabel));
    return Array.from(labels);
  }, [rawApplications, rawArchivedApplications]);

  const filteredActive = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return rawApplications.filter((app) => {
      const label = getPropertyLabel(app);
      const matchesProperty = propertyFilter === 'all' || label === propertyFilter;
      const matchesSearch = !term
        || app.full_name.toLowerCase().includes(term)
        || label.toLowerCase().includes(term);
      return matchesProperty && matchesSearch;
    });
  }, [rawApplications, propertyFilter, searchTerm]);

  const filteredArchived = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return rawArchivedApplications.filter((app) => {
      const label = getPropertyLabel(app);
      const matchesProperty = propertyFilter === 'all' || label === propertyFilter;
      const matchesSearch = !term
        || app.full_name.toLowerCase().includes(term)
        || label.toLowerCase().includes(term);
      return matchesProperty && matchesSearch;
    });
  }, [rawArchivedApplications, propertyFilter, searchTerm]);

  const groupedActive = useMemo(() => groupApplications(filteredActive), [filteredActive]);
  const groupedArchived = useMemo(() => groupApplications(filteredArchived), [filteredArchived]);

  if (loading) return <main className="App-main">Loading applicants...</main>;
  if (error) return <main className="App-main"><p className="error-message">{error}</p></main>;

  const handleArchive = async (appId) => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    if (!window.confirm('Archive this application? You can still access it later if needed.')) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/applications/${appId}/archive`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to archive application');
      setRawApplications((prev) => {
        const archivedApp = prev.find((app) => app.id === appId);
        const next = prev.filter((app) => app.id !== appId);
        if (archivedApp) {
          setRawArchivedApplications((archPrev) => [archivedApp, ...archPrev]);
        }
        return next;
      });
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRestore = async (appId) => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/applications/${appId}/unarchive`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to restore application');
      setRawArchivedApplications((archPrev) => {
        const restoredApp = archPrev.find((app) => app.id === appId);
        const nextArchived = archPrev.filter((app) => app.id !== appId);
        if (restoredApp) {
          setRawApplications((prev) => [restoredApp, ...prev]);
        }
        return nextArchived;
      });
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <main className="App-main">
      <div className="applicant-module">
      <div className="ledger-header" style={{ justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
        <div>
          <p className="eyebrow-tag">Applications</p>
          <h2>My Applicants</h2>
          <p>Review applicants by property, filter by status, and jump straight into each dossier.</p>
        </div>
      </div>

      <div className="applicants-filters">
        <input
          type="search"
          className="form-control"
          placeholder="Search applicant or property"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select className="form-control" value={propertyFilter} onChange={(e) => setPropertyFilter(e.target.value)}>
          <option value="all">All properties</option>
          {propertyOptions.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      </div>

      {!hideNewItems && unreadCount > 0 && latestApplications.length > 0 && (
        <div className="dashboard-section new-items-card" style={{ borderLeft: '4px solid #2f6fed', background: '#f5f8ff' }}>
          <div className="section-header">
            <h3>New items</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span className="report-chip report-chip--muted">Latest submissions</span>
              <button
                className="card-dismiss"
                onClick={() => { localStorage.setItem('dismiss_newitems_landlord_applicants', '1'); setHideNewItems(true); }}
                aria-label="Dismiss"
              >
                Dismiss
              </button>
            </div>
          </div>
        {latestApplications.length === 0 ? (
          <p className="status-text">No applicant activity yet.</p>
        ) : (
          <div className="inquiry-list">
            {latestApplications.map((app) => {
              const isNew = lastViewedAt && new Date(app.created_at) > new Date(lastViewedAt);
              return (
                <div key={`latest-${app.id}`} className="inquiry-card">
                  <p style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                    <strong>{app.full_name}</strong>
                    {app.identity_status === 'verified' && <span className="report-chip report-chip--success" style={{ fontSize: '0.7rem', padding: '0.15rem 0.5rem' }}>✓ ID</span>}
                    • {app.property_address}
                  </p>
                  <p className="status-text">Status: {statusLabels[app.status] || app.status}</p>
                  <p className="timestamp">{new Date(app.created_at).toLocaleString()}</p>
                  <div className="form-actions" style={{ marginTop: '0.5rem', gap: '0.5rem' }}>
                    <Link className="secondary-button" to={`/landlord/application/${app.id}`}>Review application</Link>
                    {['approved', 'lease_tenant_signed', 'lease_drafted_landlord_signed', 'completed'].includes(app.status) && (
                      <Link to={`/landlord/applications/${app.id}/lease`} className="primary-button" style={{ fontSize: '0.82rem', padding: '0.35rem 0.65rem' }}>
                        {app.status === 'completed' ? 'View Lease' : 'Lease & Signing'}
                      </Link>
                    )}
                    {isNew && <span className="report-chip" style={{ marginLeft: '0.5rem' }}>New</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      )}

      {!showArchived && (
        <div key="active" className="archive-fade">
        {Object.keys(groupedActive).length > 0 ? (
          Object.entries(groupedActive).map(([property, apps]) => (
            <div key={property} className="dashboard-section">
              <div className="section-header">
                <h3>{property}</h3>
                <span className="report-chip report-chip--muted">{apps.length} applicant{apps.length === 1 ? '' : 's'}</span>
              </div>
              <div className="inquiry-list">
                {apps.map((app) => (
                  <div key={app.id} className="inquiry-card applicant-card-row">
                    <div className="applicant-card-row__status-bar">
                      <span className={getStatusChipClass(app.status)}>{statusLabels[app.status] || app.status}</span>
                    </div>
                    <div className="applicant-card-row__primary">
                      <p className="eyebrow-tag">Submitted {new Date(app.created_at).toLocaleDateString()}</p>
                      <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                        {app.full_name}
                        {app.identity_status === 'verified' && <span className="report-chip report-chip--success" style={{ fontSize: '0.7rem', padding: '0.15rem 0.5rem', fontWeight: 500 }}>✓ ID Verified</span>}
                      </h4>
                      <p className="status-text">{app.tenant_email}</p>
                    </div>
                    <div className="applicant-card-row__meta">
                      <span>Phone: {app.phone_number || '—'}</span>
                      {app.move_in_date && <span>Move-in {new Date(app.move_in_date).toLocaleDateString()}</span>}
                      {app.employment_status && <span>{app.employment_status}</span>}
                    </div>
                    <div className="form-actions" style={{ marginTop: '0.5rem', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <Link to={`/landlord/application/${app.id}`} className="secondary-button">Review</Link>
                      {['approved', 'lease_tenant_signed', 'lease_drafted_landlord_signed', 'completed'].includes(app.status) && (
                        <Link to={`/landlord/applications/${app.id}/lease`} className="primary-button" style={{ fontSize: '0.82rem', padding: '0.35rem 0.65rem' }}>
                          {app.status === 'completed' ? 'View Lease' : 'Lease'}
                        </Link>
                      )}
                      <button type="button" className="ghost-button danger-link" onClick={() => handleArchive(app.id)}>
                        Archive
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        ) : (
          <p>No applicants yet.</p>
        )}
        </div>
      )}

      {showArchived && (
        <div className="dashboard-section" style={{ marginTop: '1.5rem' }}>
          <div className="section-header">
            <h3>Archived applicants</h3>
            <span className="report-chip report-chip--muted">Hidden from active list</span>
          </div>
          {Object.keys(groupedArchived).length > 0 ? (
            Object.entries(groupedArchived).map(([property, apps]) => (
              <div key={`archived-${property}`} style={{ marginTop: '1rem' }}>
                <div className="section-header" style={{ marginBottom: '0.5rem' }}>
                  <h4>{property}</h4>
                  <span className="report-chip report-chip--muted">{apps.length} applicant{apps.length === 1 ? '' : 's'}</span>
                </div>
                <div className="inquiry-list">
                  {apps.map((app) => (
                    <div key={`archived-${app.id}`} className="inquiry-card applicant-card-row">
                      <p className="eyebrow-tag applicant-card-row__submitted">Submitted {new Date(app.created_at).toLocaleDateString()}</p>
                      <div className="applicant-card-row__status-bar">
                        <span className={getStatusChipClass(app.status)}>{statusLabels[app.status] || app.status}</span>
                      </div>
                      <div className="applicant-card-row__primary">
                        <h4>{app.full_name}</h4>
                        <p className="status-text">{app.tenant_email}</p>
                      </div>
                      <div className="applicant-card-row__meta">
                        <span>Phone: {app.phone_number || '—'}</span>
                        {app.move_in_date && <span>Move-in {new Date(app.move_in_date).toLocaleDateString()}</span>}
                        {app.employment_status && <span>{app.employment_status}</span>}
                      </div>
                      <div className="form-actions" style={{ marginTop: '0.5rem', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <Link to={`/landlord/application/${app.id}`} className="secondary-button">Review</Link>
                        <button type="button" className="ghost-button" onClick={() => handleRestore(app.id)}>
                          Restore
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <p className="status-text">No archived applicants yet.</p>
          )}
        </div>
      )}

      <div className="form-actions" style={{ marginTop: '1.5rem' }}>
        <button
          type="button"
          className="secondary-button"
          onClick={() => setShowArchived(prev => !prev)}
        >
          {showArchived ? 'Hide archived' : `View archived${archivedCount ? ` (${archivedCount})` : ''}`}
        </button>
      </div>

      </div>
    </main>
  );
}

export default ApplicantList;
